export function getDocumentLocale() {
  if (document.documentElement.lang) {
    return document.documentElement.lang;
  }
  const { navigator } = window;
  if (navigator && navigator.language) {
    return navigator.language;
  }
  return null;
}

export function sniffLocaleFromCallback() {
  const callback = window.onBambuserLiveShoppingReady;
  if (typeof callback !== 'function') return null;

  try {
    const code = callback.toString();

    // This is a best-effort source sniff, not JavaScript parsing. A commented
    // out `player.configure({ locale: '...' })` can still be matched.
    // Identify the parameter name (the "player" variable) across all common
    // callback shapes: `function (p)`, `function name(p)`, `(p) =>`, `p =>`,
    // and async variants. The capture group is restricted to identifier
    // characters so leading `(` cannot leak in for function-keyword forms.
    const paramMatch = code.match(/^(?:async\s+)?(?:function\s*[\w$]*\s*)?\(?\s*([\w$]+)/);
    if (!paramMatch) return null;

    const playerVarName = paramMatch[1];

    // Build a dynamic Regex using that variable name
    // We escape the name just in case they used something weird like '$'
    const escapedName = playerVarName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Look for [varName].configure({...})
    const configRegex = new RegExp(`${escapedName}\\.configure\\s*\\(\\s*(\\{[\\s\\S]*?\\})\\s*\\)`);
    const configMatch = code.match(configRegex);

    if (!configMatch) return null;

    const objectString = configMatch[1];

    // Extract the locale value
    const localeMatch = objectString.match(/locale\s*:\s*["'`]([a-zA-Z-]+)["'`](,?)/);

    return localeMatch ? localeMatch[1] : null;
  } catch (err) {
    console.warn('Error sniffing locale from callback:', err);
  }

  return null;
}

export function getLocale() {
  const callbackLocale = sniffLocaleFromCallback();
  if (callbackLocale) {
    return callbackLocale;
  }
  return getDocumentLocale();
}

// When the merchant sets CC to "on" without pinning a language, the page's own
// locale is the best signal for which subtitle track to prefer. Bare language
// subtags (e.g. "en") need a region tie-breaker to land on a CC_LOCALES entry.
const PREFERRED_REGION_BY_LANGUAGE = {
  ar: 'SA',
  ckb: 'IQ',
  de: 'DE',
  en: 'US',
  es: 'ES',
  fr: 'FR',
  pt: 'PT',
  sw: 'KE',
  zh: 'CN',
};

const PREFERRED_REGION_BY_LANGUAGE_AND_SCRIPT = {
  zh: {
    Hans: 'CN',
    Hant: 'TW',
  },
};

export function matchCcLocale(rawLocale, ccLocales) {
  if (!rawLocale || !ccLocales) return null;
  const locales = Array.isArray(ccLocales) || typeof ccLocales.forEach === 'function'
    ? Array.from(ccLocales)
    : [];
  if (!locales.length) return null;

  const normalized = String(rawLocale).replace(/_/g, '-').trim();
  if (!normalized) return null;

  const findLocale = (candidate) =>
    locales.find((entry) => String(entry).toLowerCase() === candidate.toLowerCase()) || null;

  const [rawLanguage, ...rawSubtags] = normalized.split('-').filter(Boolean);
  if (!rawLanguage) return null;

  const lowerLanguage = rawLanguage.toLowerCase();
  const region = rawSubtags.find((subtag) => /^[a-z]{2}$/i.test(subtag) || /^\d{3}$/.test(subtag));
  const script = rawSubtags.find((subtag) => /^[a-z]{4}$/i.test(subtag));
  const canonicalRegion = region ? region.toUpperCase() : null;
  const canonicalScript = script
    ? `${script[0].toUpperCase()}${script.slice(1).toLowerCase()}`
    : null;

  const exactMatch = [
    normalized,
    [lowerLanguage, canonicalScript, canonicalRegion].filter(Boolean).join('-'),
    canonicalRegion ? `${lowerLanguage}-${canonicalRegion}` : null,
  ]
    .filter(Boolean)
    .map(findLocale)
    .find(Boolean);

  if (exactMatch) {
    return exactMatch;
  }

  const preferred = PREFERRED_REGION_BY_LANGUAGE_AND_SCRIPT[lowerLanguage]?.[canonicalScript]
    || PREFERRED_REGION_BY_LANGUAGE[lowerLanguage];
  if (preferred) {
    const candidate = `${lowerLanguage}-${preferred}`;
    const match = findLocale(candidate);
    if (match) return match;
  }

  // If exactly one regional variant exists for this language, use it. When
  // multiple variants exist without a preference signal, returning the first by
  // Set insertion order would silently depend on CC_LOCALES ordering — prefer
  // null so the caller falls back to no default CC.
  const languageMatches = locales.filter(
    (entry) => String(entry).split('-')[0].toLowerCase() === lowerLanguage,
  );
  return languageMatches.length === 1 ? languageMatches[0] : null;
}
